<template>
    <div class="flex flex-col items-start gap-2">
        <details v-if="data.thinking" class="max-w-[80%] text-xs text-slate-500">
            <summary class="cursor-pointer hover:text-slate-700">Thoughts</summary>
            <div class="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 px-3 py-2 italic">{{ data.thinking }}</div>
        </details>

        <div v-if="inRolePlay && personaName" class="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-violet-700" data-testid="role-play-persona-tag">
            <span class="rounded-full bg-violet-100 px-2 py-0.5">In character: {{ personaName }}</span>
        </div>

        <div
            v-if="data.content"
            :class="[
                'max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm shadow-sm ring-1',
                inRolePlay ? 'bg-violet-50 text-violet-950 ring-violet-200' : 'bg-white text-slate-900 ring-slate-200',
            ]">
            {{ data.content }}
        </div>

        <div v-if="data.tool_calls && data.tool_calls.length" class="flex flex-wrap gap-2">
            <span v-for="call in data.tool_calls" :key="call.id" class="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
                → {{ call.function?.name ?? "(tool)" }}
            </span>
        </div>

        <div class="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-400">
            <span
                >{{ data.model }}<span v-if="data.stop_reason"> · {{ data.stop_reason }}</span></span
            >
            <button
                v-if="feedbackEnabled && eventId && !isPending"
                type="button"
                class="rounded-sm px-1 text-[10px] text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                data-testid="feedback-toggle"
                :title="open ? 'Cancel feedback' : 'Leave feedback on this turn'"
                @click="toggle">
                🚩 feedback
            </button>
            <button
                v-if="callLogsEnabled && eventId && !isPending"
                type="button"
                class="rounded-sm px-1 text-[10px] text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                data-testid="call-logs-toggle"
                title="View LLM call logs for this turn"
                @click="eventId && emit('view-call-logs', eventId)">
                📋 call logs
            </button>
        </div>

        <div v-if="open" class="w-full max-w-[80%] rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div class="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
                <span class="font-medium">Verdict</span>
                <button
                    v-for="option in verdictOptions"
                    :key="option.value"
                    type="button"
                    class="rounded px-2 py-0.5 text-[11px] hover:bg-white"
                    :class="verdict === option.value ? 'bg-white ring-1 ring-slate-300 text-slate-900' : 'text-slate-500'"
                    :data-testid="`feedback-verdict-${option.value}`"
                    @click="setVerdict(option.value)">
                    {{ option.label }}
                </button>
            </div>
            <textarea
                v-model="text"
                rows="3"
                aria-label="Feedback"
                placeholder="What felt off (or right) about this turn?"
                class="w-full resize-none rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:border-slate-500 focus:outline-none disabled:bg-slate-100"
                data-testid="feedback-text"
                :disabled="submitting"
                @keydown.meta.enter.exact.prevent="onSubmit"
                @keydown.ctrl.enter.exact.prevent="onSubmit"></textarea>
            <div class="mt-2 flex items-center justify-between gap-2">
                <span v-if="error" class="text-xs text-rose-600">{{ error }}</span>
                <span v-else class="text-[10px] text-slate-400">Cmd/Ctrl+Enter to submit</span>
                <div class="flex items-center gap-2">
                    <button type="button" class="rounded px-2 py-1 text-xs text-slate-500 hover:bg-white" data-testid="feedback-cancel" :disabled="submitting" @click="cancel">
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="rounded bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                        data-testid="feedback-submit"
                        :disabled="submitting || text.trim().length === 0"
                        @click="onSubmit">
                        {{ submitting ? "Saving…" : "Save" }}
                    </button>
                </div>
            </div>
        </div>

        <div
            v-for="(entry, i) in feedback"
            :key="i"
            class="max-w-[80%] rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900"
            data-testid="feedback-annotation">
            <span class="mr-1 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                Feedback<span v-if="entry.verdict"> · {{ entry.verdict }}</span>
            </span>
            <span class="whitespace-pre-wrap">{{ entry.feedback_text }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { logError } from "~vue/utils/logError";
import { ref } from "vue";

import { type FeedbackVerdict, submitFeedback } from "./feedback";
import type { AssistantMessageData, DeveloperFeedbackData } from "./types";

const props = defineProps<{
    data: AssistantMessageData;
    eventId?: string | null;
    conversationId?: string | null;
    feedbackEnabled?: boolean;
    callLogsEnabled?: boolean;
    feedback?: DeveloperFeedbackData[];
    isPending?: boolean;
    // Set when a role-play is active at this message's position in the log.
    // When true, the bubble swaps to violet chrome and shows the persona
    // name, so the user can see at a glance that Nadia is speaking in
    // character. The parent derives this from ROLE_PLAY_STARTED/ENDED events.
    inRolePlay?: boolean;
    personaName?: string | null;
}>();

const emit = defineEmits<{
    (e: "feedback-saved", payload: { eventId: string; feedback: DeveloperFeedbackData }): void;
    (e: "view-call-logs", eventId: string): void;
}>();

const verdictOptions: Array<{ value: FeedbackVerdict; label: string }> = [
    { value: "good", label: "👍 good" },
    { value: "bad", label: "👎 bad" },
    { value: "note", label: "📝 note" },
];

const open = ref(false);
const text = ref("");
const verdict = ref<FeedbackVerdict | null>(null);
const submitting = ref(false);
const error = ref<string | null>(null);

const toggle = () => {
    open.value = !open.value;
    if (!open.value) {
        text.value = "";
        verdict.value = null;
        error.value = null;
    }
};

const cancel = () => {
    open.value = false;
    text.value = "";
    verdict.value = null;
    error.value = null;
};

const setVerdict = (v: FeedbackVerdict) => {
    verdict.value = verdict.value === v ? null : v;
};

const onSubmit = async () => {
    if (!props.conversationId || !props.eventId) {
        return;
    }
    const trimmed = text.value.trim();
    if (!trimmed) {
        return;
    }
    submitting.value = true;
    error.value = null;
    try {
        await submitFeedback(props.conversationId, {
            feedbackText: trimmed,
            targetEventId: props.eventId,
            verdict: verdict.value,
        });
        emit("feedback-saved", {
            eventId: props.eventId,
            feedback: {
                target_event_id: props.eventId,
                feedback_text: trimmed,
                verdict: verdict.value,
                author_user_id: "",
            },
        });
        cancel();
    } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        error.value = e.message;
        logError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
